// app/src/Account.jsx — user account & business profile (#/account).
//
// Home for the restaurant ZIP (market area for cross-restaurant price
// monitoring), current plan, and sign-out. Reached via the avatar chip.

// Offer "Add to home screen". On Chromium the beforeinstallprompt event was
// stashed at boot (window.kanjoInstall); on iOS Safari there's no programmatic
// install, so we show the manual Share-sheet hint instead. Hidden once installed.
// Tiny relative-time chip used by the autosave indicator. Ticks every 10s
// so "Saved just now" → "5s ago" → "1m ago" without polling JSON.
function RelTime({ ts }) {
  const [, force] = React.useState(0);
  React.useEffect(() => {
    const i = setInterval(() => force((n) => n + 1), 10_000);
    return () => clearInterval(i);
  }, []);
  if (!ts) return null;
  const sec = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (sec < 8) return <>just now</>;
  if (sec < 60) return <>{sec}s ago</>;
  const min = Math.round(sec / 60);
  if (min < 60) return <>{min}m ago</>;
  return <>{Math.round(min / 60)}h ago</>;
}

function InstallSection() {
  useT();
  const standalone = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    || window.navigator.standalone === true;
  const [installed, setInstalled] = React.useState(standalone);
  const [canInstall, setCanInstall] = React.useState(!!window.kanjoInstallReady);
  React.useEffect(() => {
    const onBip = () => setCanInstall(true);
    const onInstalled = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    if (window.kanjoInstallReady) setCanInstall(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);
  const onInstall = async () => {
    try { if (await window.kanjoInstall?.()) setInstalled(true); }
    catch (e) { console.warn('[install] prompt failed', e); }
  };
  if (installed) return null;
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent || '');
  return (
    <section>
      <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.installApp', 'Install app')}</div>
      {canInstall ? (
        <button onClick={onInstall}
          style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: 'var(--accent,#5b8def)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
          {t('account.installCTA', 'Add Kanjo to your home screen')}
        </button>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--on-soft)', margin: 0, lineHeight: 1.45 }}>
          {t('account.installNote')}
        </p>
      )}
    </section>
  );
}

// Translate an openLink/disconnect rejection into a user-facing line. Plaid
// Link rejects with 'plaid: user cancelled' on a normal exit (not an error —
// show nothing), and requestLinkToken throws 'plaid: link-token 402' when the
// plan's connected-institution cap is hit.
function friendlyBankError(e) {
  const m = String((e && e.message) || e || '');
  if (/cancelled/i.test(m)) return '';
  if (/402/.test(m)) return t('bank.capReached', "You've reached your plan's bank-connection limit. Upgrade to connect more.");
  return t('bank.connectFailed', "Couldn't connect your bank just now — please try again.");
}

// Map a Plaid Item status to a display badge {label, color}.
function bankStatusBadge(status) {
  switch (status) {
    case 'ITEM_LOGIN_REQUIRED': return { label: t('bank.reconnectNeeded', 'Reconnect needed'), color: 'var(--danger)' };
    case 'PENDING_EXPIRATION':  return { label: t('bank.reconnectSoon', 'Reconnect soon'), color: 'var(--warning,#c9941f)' };
    case 'REVOKED':             return { label: t('bank.disconnected', 'Disconnected'), color: 'var(--ink-3,#999)' };
    default:                    return { label: t('bank.connected', 'Connected'), color: 'var(--positive,#2e9e5b)' };
  }
}

// Bank connections (Plaid). Pro / à-la-carte / admin only — Basic operators use
// the CSV importer on the Cash flow screen instead, and free has no bank data.
// Connecting is hosted entirely by Plaid Link (window.kanjo.plaid.openLink); the
// PWA never sees credentials or the access_token, only non-sensitive metadata.
// Lists connected institutions with health + accounts, and offers relink (when
// an Item needs re-auth) and disconnect. Renders nothing for ineligible users.
// ── Book encryption (solo end-to-end; owner decision 2026-07-11) ─────────────
// Opt-in: turning it on REQUIRES setting a recovery phrase (the phrase escrows
// this device's crypto salt, so a new phone can still restore — recoverability
// is structural, not a warning label). While the account has team members the
// books upload readable-to-the-service regardless (viewers must load them);
// encryption resumes automatically when the last member is removed.
function BooksEncryptionPanel() {
  useT();
  const [st, setSt] = React.useState({ loading: true, enabled: false, members: 0 });
  const [open, setOpen] = React.useState(false);
  const [p1, setP1] = React.useState('');
  const [p2, setP2] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [msg, setMsg] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const claims = await window.kanjo.auth.currentClaims().catch(() => null);
      if (!claims) { setSt({ loading: false, enabled: false, members: 0 }); return; }
      const enabled = await window.kanjo.backup.booksEncryptionEnabled();
      // Team membership is server-side; fall back to the last-known count offline.
      let members = 0;
      try {
        const s = await window.kanjo.scale.team.summary();
        members = (s?.members?.length || 0) + (s?.invites?.length || 0);
      } catch {
        const prof = await window.kanjo.db.userProfile.get(claims.sub).catch(() => null);
        members = typeof prof?.teamMemberCount === 'number' ? prof.teamMemberCount : 0;
      }
      setSt({ loading: false, enabled, members });
    } catch { setSt({ loading: false, enabled: false, members: 0 }); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const enable = async () => {
    setErr(null);
    if (p1.trim().length < 8) { setErr(t('account.encPhraseShort', 'The recovery phrase needs at least 8 characters.')); return; }
    if (p1 !== p2) { setErr(t('account.encPhraseMismatch', 'The phrases don’t match.')); return; }
    setBusy(true);
    try {
      await window.kanjo.backup.enableBooksEncryption(p1);
      setP1(''); setP2(''); setOpen(false);
      setMsg(t('account.encEnabledMsg', 'Book encryption is on.'));
      await load();
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm(t('account.encDisableConfirm', 'Turn off book encryption? Your books will be stored readable to the Kanjo service again (still encrypted at rest on our side).'))) return;
    setBusy(true); setErr(null);
    try { await window.kanjo.backup.disableBooksEncryption(); setMsg(null); await load(); }
    catch (e) { setErr(String(e?.message || e)); }
    finally { setBusy(false); }
  };

  if (st.loading) return null;
  const sharing = st.members > 0;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{t('account.encTitle', 'Book encryption')}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.45 }}>
            {st.enabled
              ? (sharing
                ? t('account.encPausedTeam', 'Paused while your team has access — teammates need readable books. Resumes when the last member is removed.')
                : t('account.encOnBody', 'On — your books are encrypted before they leave this phone. Kanjo cannot read them. Your recovery phrase is the ONLY way to restore on a new phone.'))
              : t('account.encOffBody', 'Encrypt your books before they leave this phone, so only you can read them. Requires a recovery phrase — without it, a lost phone means lost books.')}
          </div>
        </div>
        {st.enabled
          ? <button className="btn" disabled={busy} onClick={disable} style={{ flexShrink: 0 }}>{t('account.encTurnOff', 'Turn off')}</button>
          : <button className="btn primary" disabled={busy} onClick={() => { setErr(null); setOpen(true); }} style={{ flexShrink: 0 }}>{t('account.encTurnOn', 'Turn on')}</button>}
      </div>
      {msg && <div style={{ fontSize: 12.5, color: 'var(--positive)', marginTop: 8 }}>{msg}</div>}
      {err && !open && <div style={{ fontSize: 12.5, color: 'var(--danger)', marginTop: 8 }}>{err}</div>}

      <Sheet open={open} onClose={() => setOpen(false)} title={t('account.encSheetTitle', 'Set your recovery phrase')}>
        <div style={{ padding: '4px 16px 20px' }}>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, margin: '0 0 8px' }}>
            {t('account.encSheetBody', 'This phrase is the only way to restore your encrypted books on a new phone. Kanjo cannot reset it or recover your books without it — write it down somewhere safe.')}
          </p>
          <input type="password" autoComplete="new-password" placeholder={t('account.encPhrase', 'Recovery phrase (8+ characters)')}
            value={p1} onChange={(e) => setP1(e.target.value)} aria-label={t('account.encPhrase', 'Recovery phrase (8+ characters)')}
            style={{ width: '100%', padding: '11px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)', marginBottom: 8 }}/>
          <input type="password" autoComplete="new-password" placeholder={t('account.encPhrase2', 'Repeat the phrase')}
            value={p2} onChange={(e) => setP2(e.target.value)} aria-label={t('account.encPhrase2', 'Repeat the phrase')}
            onKeyDown={(e) => { if (e.key === 'Enter') enable(); }}
            style={{ width: '100%', padding: '11px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--rule-2)', background: 'var(--surface)', color: 'var(--ink)' }}/>
          {err && <div style={{ fontSize: 12.5, color: 'var(--danger)', marginTop: 8 }}>{err}</div>}
          <button className="btn primary" disabled={busy} onClick={enable} style={{ width: '100%', marginTop: 12 }}>
            {busy ? '…' : t('account.encEnable', 'Encrypt my books')}
          </button>
        </div>
      </Sheet>
    </div>
  );
}

function BankConnectionsSection() {
  useT();
  const [state, setState] = React.useState({ loading: true, eligible: false, userId: null, items: [] });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  const loadItems = React.useCallback(async (userId) => {
    const plaid = window.kanjo?.plaid;
    const db = window.kanjo?.db;
    if (!plaid || !db || !userId) return [];
    const items = await plaid.listInstitutions(userId);
    // Hide the synthetic "upload" pseudo-item the CSV importer creates — it's
    // not a Plaid connection and has nothing to relink or revoke.
    const real = items.filter((it) => it.plaidItemId !== 'upload');
    const accounts = await db.plaidAccounts.where('userId').equals(userId).toArray();
    return real.map((it) => ({ ...it, accounts: accounts.filter((a) => a.plaidItemId === it.plaidItemId) }));
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
        if (!claims || claims['custom:role'] === 'viewer') {
          if (!cancelled) setState({ loading: false, eligible: false, userId: null, items: [] });
          return;
        }
        const tier = String(claims['custom:tier'] || 'free').toLowerCase();
        let groups = claims['cognito:groups'];
        if (typeof groups === 'string') groups = groups.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
        const isAdmin = Array.isArray(groups) && groups.includes('admins');
        const eligible = isAdmin || tier === 'pro' || tier === 'payg';
        if (!eligible) {
          if (!cancelled) setState({ loading: false, eligible: false, userId: null, items: [] });
          return;
        }
        const items = await loadItems(claims.sub);
        if (!cancelled) setState({ loading: false, eligible: true, userId: claims.sub, items });
      } catch {
        if (!cancelled) setState({ loading: false, eligible: false, userId: null, items: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [loadItems]);

  const reload = async () => {
    if (!state.userId) return;
    try { const items = await loadItems(state.userId); setState((s) => ({ ...s, items })); }
    catch { /* keep prior list */ }
  };

  const onConnect = async () => {
    setErr(''); setBusy(true);
    try { window.kanjo.track('plaid_connect_start'); } catch { /* ignore */ }
    try {
      await window.kanjo.plaid.openLink();
      // The exchange Lambda already wrote the initial-sync WAL; pull it into
      // Dexie now and reconcile so the new accounts/transactions show at once.
      try { await window.kanjo.plaid.mergePendingWal(); await window.kanjo.plaid.reconcileAll(); }
      catch (e) { console.warn('[bank] post-connect sync failed', e?.message || e); }
      await reload();
      try { window.kanjo.track('plaid_connect_success'); } catch { /* ignore */ }
    } catch (e) {
      // A normal "user cancelled" exit is not a failure — don't count it.
      try { if (!/cancelled/i.test(String(e?.message || e))) window.kanjo.track('plaid_connect_error'); } catch { /* ignore */ }
      setErr(friendlyBankError(e));
    }
    finally { setBusy(false); }
  };

  const onReconnect = async (itemId) => {
    setErr(''); setBusy(true);
    try { await window.kanjo.plaid.openLinkUpdateMode(itemId); await reload(); }
    catch (e) { setErr(friendlyBankError(e)); }
    finally { setBusy(false); }
  };

  const onDisconnect = async (itemId, name) => {
    const msg = t('bank.disconnectConfirm', 'Disconnect {name}? Its transactions will be removed from this device.').replace('{name}', name || t('bank.thisBank', 'this bank'));
    if (!window.confirm(msg)) return;
    setErr(''); setBusy(true);
    try { await window.kanjo.plaid.disconnectInstitution(itemId); await reload(); }
    catch (e) {
      setErr(/502/.test(String(e?.message)) ? t('bank.revokeFailed', "Couldn't disconnect right now — please try again.") : friendlyBankError(e));
    } finally { setBusy(false); }
  };

  if (state.loading || !state.eligible) return null;

  return (
    <section>
      <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('bank.connections', 'Bank connections')}</div>

      {state.items.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--on-soft)', margin: '0 0 12px', lineHeight: 1.45 }}>
          {t('bank.empty', 'Connect your bank to pull transactions automatically and keep your cash position live. Your login is handled by Plaid — Kanjo never sees it.')}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
          {state.items.map((it) => {
            const badge = bankStatusBadge(it.status);
            const needsRelink = it.status && it.status !== 'HEALTHY';
            return (
              <div key={it.plaidItemId} style={{ padding: '14px 16px', background: 'var(--surface-2,#f5f5f7)', borderRadius: 12 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink,#111)' }}>{it.institutionName || t('bank.unknown', 'Bank')}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: badge.color, whiteSpace: 'nowrap' }}>{badge.label}</span>
                </div>
                {it.accounts.length > 0 && (
                  <div style={{ fontSize: 12.5, color: 'var(--ink-2,#666)', marginTop: 6, lineHeight: 1.5 }}>
                    {it.accounts.map((a) => (
                      <div key={a.plaidAccountId}>{a.name || a.subtype || t('bank.account', 'Account')}{a.mask ? ` ···· ${a.mask}` : ''}</div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-3,#999)' }}>
                    {it.lastSyncedAt ? <>{t('bank.synced', 'Synced')} <RelTime ts={it.lastSyncedAt}/></> : t('bank.notSyncedYet', 'Not synced yet')}
                  </span>
                  <span style={{ display: 'flex', gap: 14 }}>
                    {needsRelink && (
                      <button type="button" onClick={() => onReconnect(it.plaidItemId)} disabled={busy}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--accent,#5b8def)' }}>
                        {t('bank.reconnect', 'Reconnect')}
                      </button>
                    )}
                    <button type="button" onClick={() => onDisconnect(it.plaidItemId, it.institutionName)} disabled={busy}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: 'var(--ink-3,#999)' }}>
                      {t('bank.disconnect', 'Disconnect')}
                    </button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button type="button" onClick={onConnect} disabled={busy}
        style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: 'var(--accent,#5b8def)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
        {busy ? t('bank.connecting', 'Connecting…') : (state.items.length > 0 ? t('bank.connectAnother', 'Connect another bank') : t('bank.connect', 'Connect a bank'))}
      </button>
      {err && <ErrorBanner style={{ marginTop: 10 }}>{err}</ErrorBanner>}
    </section>
  );
}

function AccountScreen({ dark = false, persona = 'owner' }) {
  useT();
  const nav = React.useContext(KanjoNav);
  const [zip, setZip] = React.useState('');
  const [savedZip, setSavedZip] = React.useState('');
  const [tier, setTier] = React.useState('free');
  const [shareData, setShareData] = React.useState(false);
  const [shareSaving, setShareSaving] = React.useState(false);
  const [businessType, setBusinessType] = React.useState('');
  const [businessTypeSaving, setBusinessTypeSaving] = React.useState(false);
  const [servesAlcohol, setServesAlcohol] = React.useState('');
  const [servesAlcoholSaving, setServesAlcoholSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [role, setRole] = React.useState('owner');
  const [adminEmail, setAdminEmail] = React.useState(null);
  const [savedAt, setSavedAt] = React.useState(null); // ms timestamp of last ZIP save
  const [privOpen, setPrivOpen] = React.useState(false);

  React.useEffect(() => {
    window.kanjo?.scale?.account?.currentAccount?.().then((a) => {
      setRole(a?.role || 'owner');
      setAdminEmail(a?.adminEmail || null);
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    window.kanjo?.scale?.profile?.getProfile?.()
      .then((p) => { if (p) { setZip(p.zip || ''); setSavedZip(p.zip || ''); setTier(p.tier || 'free'); setShareData(p.shareData === true); setBusinessType(p.businessType || ''); setServesAlcohol(p.servesAlcohol || ''); } })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setErr(null); setMsg(null);
    try {
      const r = await window.kanjo.scale.profile.saveZip(zip.trim());
      setZip(r.zip); setSavedZip(r.zip); setSavedAt(Date.now());
    } catch (e) {
      setErr(e.status === 400 ? 'Enter a valid 5-digit ZIP code.' : 'Could not save — try again.');
    } finally { setSaving(false); }
  };
  const onZipBlur = () => {
    const v = zip.trim();
    if (v.length === 5 && v !== savedZip) save();
  };

  // Business type persists on change; revert on failure to keep the picker
  // in sync with the server. The selected type drives which OCR vocabulary
  // the receipt pipeline uses (today: only 'restaurant' is implemented; the
  // rest fall back to the restaurant vocab until per-type vocabs ship).
  const changeBusinessType = async (next) => {
    const prev = businessType;
    setBusinessType(next); setBusinessTypeSaving(true); setErr(null); setMsg(null);
    try {
      const r = await window.kanjo.scale.profile.saveProfile({ businessType: next });
      setBusinessType(r.businessType || '');
    } catch (e) {
      setBusinessType(prev);
      setErr('Could not save business type — try again.');
    } finally { setBusinessTypeSaving(false); }
  };

  // Alcohol capability: 'none' | 'beer-wine' | 'full-bar'. Composed onto
  // the base businessType vocab — splits wine / beer / spirits out of the
  // generic beverages bucket so pour cost is computable. No-op for bar
  // (already splits) and businessTypes whose vocab handles alcohol natively.
  const changeServesAlcohol = async (next) => {
    const prev = servesAlcohol;
    setServesAlcohol(next); setServesAlcoholSaving(true); setErr(null); setMsg(null);
    try {
      const r = await window.kanjo.scale.profile.saveProfile({ servesAlcohol: next || 'none' });
      setServesAlcohol(r.servesAlcohol || '');
    } catch (e) {
      setServesAlcohol(prev);
      setErr('Could not save alcohol setting — try again.');
    } finally { setServesAlcoholSaving(false); }
  };

  // Opt-in toggle persists immediately; revert on failure so the box reflects
  // the server's actual state.
  const toggleShare = async () => {
    const next = !shareData;
    setShareData(next); setShareSaving(true); setErr(null); setMsg(null);
    try {
      const r = await window.kanjo.scale.profile.saveProfile({ shareData: next });
      setShareData(r.shareData === true);
    } catch (e) {
      setShareData(!next);
      setErr('Could not update sharing — try again.');
    } finally { setShareSaving(false); }
  };

  const signOut = async () => {
    if (!window.confirm('Sign out of Kanjo?')) return;
    try { await window.kanjo?.auth?.signOut?.(); }
    catch (e) { console.warn('[account] sign out failed', String(e?.message || e)); }
  };

  const [trial, setTrial] = React.useState(null);
  React.useEffect(() => {
    window.kanjo?.scale?.plan?.summary?.().then(setTrial).catch(() => {});
  }, []);
  const trialLabel = !trial ? 'Free trial'
    : trial.planTier === 'active' ? 'Subscribed'
    : !trial.trialStarted ? 'Free trial · starts on your first scan'
    : trial.daysLeftTrial > 0 ? `Free trial · ${trial.daysLeftTrial} day${trial.daysLeftTrial === 1 ? '' : 's'} left`
    : 'Free trial ended';
  const trialAction = 'Plans';

  const dirty = zip.trim() !== savedZip;
  // Local label for the Plan tile. Translated via the same plan.* keys as
  // the Subscribe screen so the picker and the tile read identically.
  const currentPlanLabel = tier === 'free' ? t('plan.free', 'Free')
                         : tier === 'basic' ? t('plan.basic', 'Basic')
                         : tier === 'pro' ? t('plan.pro', 'Pro')
                         : tier === 'payg' ? t('plan.aLaCarte', 'À la carte')
                         : tier;

  // Read-only viewers (team members of someone else's account) don't have a
  // plan and can't configure anything — they only view the shared books. Show a
  // minimal account screen: who they're sharing with + sign out.
  if (role === 'viewer') {
    return (
      <KShell tab="today">
        <KHeader back title={t('header.account', 'Account')} />
        <div style={{ padding: '8px 20px 40px', display: 'flex', flexDirection: 'column', gap: 26 }}>
          <section>
            <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.plan', 'Plan')}</div>
            <div style={{ padding: '14px 16px', background: 'var(--surface-2,#f5f5f7)', borderRadius: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink,#111)' }}>{t('account.shared', 'Shared Users')}</div>
              {adminEmail && (
                <div style={{ fontSize: 13, color: 'var(--ink-2,#666)', marginTop: 4 }}>
                  {t('account.sharedBy', 'Shared by')} {adminEmail}
                </div>
              )}
              <div style={{ fontSize: 12.5, color: 'var(--ink-3,#999)', marginTop: 8, lineHeight: 1.45 }}>
                {t('account.viewerBody', "You have read-only access to this account's books. Only the account administrator can change settings or invite others.")}
              </div>
            </div>
          </section>

          <button onClick={signOut}
            style={{ alignSelf: 'flex-start', padding: '10px 16px', borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--danger)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            {t('account.signOut', 'Sign out')}
          </button>
        </div>
      </KShell>
    );
  }

  return (
    <KShell tab="today">
      <KHeader back title={t('header.account', 'Account')} />
      <div style={{ padding: '8px 20px 40px', display: 'flex', flexDirection: 'column', gap: 26 }}>

        {/* Current plan — single entry into the billing screen (manage when
            subscribed, or pick a plan when not). */}
        <section>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.plan', 'Plan')}</div>
          <KLink to="/account/subscribe" asButton style={{ display: 'block' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--surface-2,#f5f5f7)', borderRadius: 12 }}>
              <span>
                <span style={{ display: 'block', fontSize: 16, fontWeight: 600, color: 'var(--ink,#111)' }}>{currentPlanLabel}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-2,#666)', marginTop: 2 }}>{trialLabel}</span>
              </span>
              <span style={{ fontSize: 13, color: 'var(--accent,#5b8def)' }}>{trial?.planTier === 'active' ? t('account.manage', 'Manage') : trialAction} ›</span>
            </div>
          </KLink>
        </section>

        {/* Bank connections (Plaid) — connect / relink / disconnect. The CSV
            transaction importer is hidden from the UI (2026-07-06) to steer
            operators to Plaid; Plaid is now the bank-data path. */}
        <BankConnectionsSection/>

        {/* Security — device lock (biometric + recovery passphrase). See
            docs/LOCAL_LOCK_DESIGN.md. SecurityPanel is defined in Lock.jsx. */}
        <section>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.security', 'Security')}</div>
          <div style={{ padding: '14px 16px', background: 'var(--surface-2,#f5f5f7)', borderRadius: 12 }}>
            <SecurityPanel/>
          </div>
          <div style={{ padding: '14px 16px', background: 'var(--surface-2,#f5f5f7)', borderRadius: 12, marginTop: 10 }}>
            <BooksEncryptionPanel/>
          </div>
        </section>

        {/* Language — UI-wide. Stored to userProfile so it follows the user
            across devices. Triggers an immediate re-render via the i18n subs. */}
        <section>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.language', 'Language')}</div>
          <div style={{ padding: '14px 16px', background: 'var(--surface-2,#f5f5f7)', borderRadius: 12 }}>
            <select value={getActiveLang()} onChange={(e) => setActiveLang(e.target.value)}
              aria-label={t('account.language', 'Language')}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 10,
                border: '1px solid var(--rule)', background: 'var(--surface)',
                color: 'var(--ink)', fontSize: 14, fontFamily: 'var(--sans)' }}>
              {getLangs().map((l) => (
                <option key={l.code} value={l.code}>
                  {(SHOW_FLAGS && l.flag) ? `${l.flag} ` : ''}{l.nativeName}{l.nativeName !== l.name ? ` — ${l.name}` : ''}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: 'var(--ink-3,#999)', marginTop: 8, lineHeight: 1.45 }}>
              {t('account.languageNote', 'App-wide. Takes effect immediately.')}
            </div>
          </div>
        </section>

        {/* Appearance — light / dark / system. Applied via .kanjo-dark on the
            document root (main.jsx); persisted to localStorage. */}
        <section>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.appearance', 'Appearance')}</div>
          <div style={{ display: 'flex', gap: 6, padding: 4, background: 'var(--surface-2,#f5f5f7)', borderRadius: 12 }}>
            {[['light', t('account.theme.light', 'Light')], ['dark', t('account.theme.dark', 'Dark')], ['system', t('account.theme.system', 'System')]].map(([val, lbl]) => {
              const on = (nav.theme || 'system') === val;
              return (
                <button key={val} type="button" onClick={() => nav.setTheme && nav.setTheme(val)}
                  aria-pressed={on}
                  style={{ flex: 1, padding: '11px 8px', borderRadius: 9, border: 'none', cursor: 'pointer',
                    fontSize: 14, fontWeight: on ? 700 : 600, fontFamily: 'var(--sans)',
                    background: on ? 'var(--brand-fill)' : 'transparent',
                    color: on ? 'var(--on-brand-fill)' : 'var(--ink-2)' }}>
                  {lbl}
                </button>
              );
            })}
          </div>
        </section>

        {/* Team — owner only */}
        {role === 'owner' && (
          <section>
            <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.team', 'Team')}</div>
            <KLink to="/account/team" asButton style={{ display: 'block' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--surface-2,#f5f5f7)', borderRadius: 12 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink,#111)' }}>{t('account.manageTeam', 'Manage team')}</span>
                <span style={{ fontSize: 13, color: 'var(--accent,#5b8def)' }}>{t('account.addViewers', 'Add viewers')} ›</span>
              </div>
            </KLink>
          </section>
        )}

        {/* Restaurant ZIP — the headline of this screen */}
        <section>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.zipTitle', 'Restaurant ZIP code')}</div>
          <p style={{ fontSize: 13, color: 'var(--on-soft)', margin: '0 0 10px', lineHeight: 1.4 }}>
            {t('account.zipBody', 'Used to compare your prices against other restaurants in your area. We only use the ZIP — never your exact address.')}
          </p>
          <input
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/[^\d]/g, '').slice(0, 5))}
            onBlur={onZipBlur}
            inputMode="numeric" maxLength={5} placeholder={t('account.zipPlaceholder', '5-digit ZIP')}
            aria-label={t('account.zipTitle', 'Restaurant ZIP code')}
            style={{ width: '100%', padding: '12px 14px', fontSize: 16, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)', boxSizing: 'border-box' }}
          />
          {/* Status line — autosave-on-blur, so the user sees confirmation
              instead of an explicit Save button. Matches the toggle pattern
              used elsewhere in the app. */}
          <div style={{ minHeight: 18, marginTop: 6, fontSize: 12.5 }}>
            {saving && <span style={{ color: 'var(--on-dim)' }}>{t('account.saving', 'Saving…')}</span>}
            {!saving && err && <span role="alert" style={{ color: 'var(--danger)', background: 'var(--danger-bg)', padding: '1px 7px', borderRadius: 6 }}>{err}</span>}
            {!saving && !err && loading && <span style={{ color: 'var(--on-dim)' }}>{t('account.loading', 'Loading…')}</span>}
            {!saving && !err && !loading && savedAt && (
              <span style={{ color: 'var(--positive,#2e9e5b)' }}>
                ✓ Saved <RelTime ts={savedAt}/>
              </span>
            )}
            {!saving && !err && !loading && !savedAt && savedZip.length === 5 && (
              <span style={{ color: 'var(--on-dim)' }}>ZIP {savedZip}</span>
            )}
          </div>
        </section>

        {/* Business type — drives the OCR categorization vocabulary for the
            receipt pipeline. Default (unset) behaves identically to
            'restaurant', so existing operators see zero change until they
            pick a different type. */}
        <section>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.businessTypeTitle', 'Business type')}</div>
          <p style={{ fontSize: 13, color: 'var(--on-soft)', margin: '0 0 10px', lineHeight: 1.4 }}>
            {t('account.businessTypeBody', 'Used to tailor receipt categorization and reports to your kind of operation.')}
          </p>
          <select
            value={businessType}
            onChange={(e) => changeBusinessType(e.target.value)}
            disabled={businessTypeSaving || loading}
            aria-label={t('account.businessTypeTitle', 'Business type')}
            style={{ width: '100%', padding: '12px 14px', fontSize: 16, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)', boxSizing: 'border-box' }}
          >
            <option value="">{t('account.businessTypeUnset', 'Choose…')}</option>
            <option value="restaurant">{t('account.btRestaurant', 'Full-service restaurant')}</option>
            <option value="qsr">{t('account.btQsr', 'Quick-service / counter restaurant')}</option>
            <option value="cafe">{t('account.btCafe', 'Café / coffee shop')}</option>
            <option value="bakery">{t('account.btBakery', 'Bakery')}</option>
            <option value="bar">{t('account.btBar', 'Bar / pub / tap room')}</option>
            <option value="brewery">{t('account.btBrewery', 'Brewery / winery / distillery')}</option>
            <option value="caterer">{t('account.btCaterer', 'Caterer')}</option>
            <option value="food-truck">{t('account.btFoodTruck', 'Food truck')}</option>
            <option value="ghost-kitchen">{t('account.btGhostKitchen', 'Ghost / cloud kitchen')}</option>
            <option value="ice-cream">{t('account.btIceCream', 'Ice cream / dessert shop')}</option>
          </select>
          <div style={{ minHeight: 18, marginTop: 6, fontSize: 12.5 }}>
            {businessTypeSaving && <span style={{ color: 'var(--on-dim)' }}>{t('account.saving', 'Saving…')}</span>}
          </div>
        </section>

        {/* Alcohol capability — composed onto the base businessType vocab so
            wine / beer / spirits split out of the generic beverages bucket.
            Restaurants with a wine list, cafés with evening service, bakeries
            with a wine bar, etc. */}
        <section>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.alcoholTitle', 'Alcohol service')}</div>
          <p style={{ fontSize: 13, color: 'var(--on-soft)', margin: '0 0 10px', lineHeight: 1.4 }}>
            {t('account.alcoholBody', 'If you serve alcohol, Kanjo separates wine / beer / spirits cost from your other beverage cost so you can track pour cost.')}
          </p>
          <select
            value={servesAlcohol}
            onChange={(e) => changeServesAlcohol(e.target.value)}
            disabled={servesAlcoholSaving || loading}
            aria-label={t('account.alcoholTitle', 'Alcohol service')}
            style={{ width: '100%', padding: '12px 14px', fontSize: 16, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)', boxSizing: 'border-box' }}
          >
            <option value="">{t('account.alcoholUnset', 'Choose…')}</option>
            <option value="none">{t('account.alcoholNone', "I don't serve alcohol")}</option>
            <option value="beer-wine">{t('account.alcoholBeerWine', 'Beer and wine only')}</option>
            <option value="full-bar">{t('account.alcoholFullBar', 'Full bar (incl. spirits)')}</option>
          </select>
          <div style={{ minHeight: 18, marginTop: 6, fontSize: 12.5 }}>
            {servesAlcoholSaving && <span style={{ color: 'var(--on-dim)' }}>{t('account.saving', 'Saving…')}</span>}
          </div>
        </section>

        {/* Price-data sharing — opt-in. The 90-word privacy paragraph used to
            sit here; collapsed to a one-liner + "Learn how" sheet so it stops
            shouting marketing copy at users skimming their settings. */}
        <section>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.sharePrice', 'Share price data')}</div>
          <p style={{ fontSize: 13, color: 'var(--on-soft)', margin: '0 0 12px', lineHeight: 1.45 }}>
            {t('account.sharePriceShort', 'Only your ZIP — never your name or address. Helps build area price trends.')}{' '}
            <button onClick={() => setPrivOpen(true)}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent,#5b8def)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {t('account.learnHow', 'Learn how')} ›
            </button>
          </p>
          <Sheet open={privOpen} onClose={() => setPrivOpen(false)} title={t('account.sharePrice', 'Share price data')}>
            <p style={{ fontSize: 14, color: 'var(--ink-2,#444)', lineHeight: 1.55, margin: 0 }}>
              {t('account.sharePriceBody')}
            </p>
          </Sheet>
          <button
            onClick={toggleShare} disabled={shareSaving}
            role="checkbox" aria-checked={shareData}
            style={{
              width: '100%', display: 'flex', alignItems: 'flex-start', gap: 12, textAlign: 'left',
              padding: '14px 16px', borderRadius: 12, cursor: shareSaving ? 'default' : 'pointer',
              background: 'var(--surface-2,#f5f5f7)', border: '1px solid ' + (shareData ? 'var(--accent,#5b8def)' : 'var(--hairline,#ddd)'),
            }}>
            <span aria-hidden="true" style={{
              width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: shareData ? 'var(--accent,#5b8def)' : 'var(--surface,#fff)',
              border: '1px solid ' + (shareData ? 'var(--accent,#5b8def)' : 'var(--ink-3,#bbb)'),
            }}>
              {shareData && <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 7.5l3.2 3.2L12 4"/></svg>}
            </span>
            <span style={{ fontSize: 14, color: 'var(--ink,#111)', lineHeight: 1.4 }}>
              <span style={{ fontWeight: 600 }}>{t('account.shareCheckLabel', 'Share my anonymized ingredient prices')}</span>
              <span style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-2,#666)', marginTop: 2 }}>
                {shareSaving ? t('account.saving', 'Saving…') : shareData ? t('account.shareOn', 'On — thank you for contributing to county price trends.') : t('account.shareOff', 'Off — your scans stay private to your account.')}
              </span>
            </span>
          </button>
        </section>

        <InstallSection/>

        {/* Legal */}
        <section>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>{t('account.legal', 'Legal')}</div>
          <div style={{ background: 'var(--surface-2,#f5f5f7)', borderRadius: 12, overflow: 'hidden' }}>
            {[
              { href: 'https://getkanjo.com/terms', label: t('account.termsLink', 'Terms of Service') },
              { href: 'https://getkanjo.com/privacy', label: t('account.privacyLink', 'Privacy Policy') },
            ].map((l, i) => (
              <a key={l.href} href={l.href} target="_blank" rel="noopener"
                 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', textDecoration: 'none', color: 'var(--ink,#111)', fontSize: 14.5, fontWeight: 500, borderTop: i > 0 ? '1px solid var(--hairline,#e5e5e5)' : 'none' }}>
                {l.label}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3,#999)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8"/></svg>
              </a>
            ))}
          </div>
          <p style={{ fontSize: 12, color: 'var(--on-dim)', margin: '8px 2px 0', lineHeight: 1.45 }}>
            {t('account.legalNote', 'Kanjo is a decision-support tool — its figures are estimates and must not be used for accounting, tax, or other filings.')}
          </p>
        </section>

        {/* Account actions */}
        <section>
          <button onClick={signOut}
            style={{ width: '100%', padding: '13px', borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--danger)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            {t('account.signOut', 'Sign out')}
          </button>
        </section>
      </div>
    </KShell>
  );
}

// Tier metadata shared by the subscribe screen (fallbacks when the lib bundle's
// TIERS aren't loaded yet, feature bullet lists, and short blurbs).
const PLAN_FALLBACK = {
  basic: { label: 'Basic', pagesPerWeek: 15, maxInstitutions: 0, monthlyCents: 500, annualCents: 5000, features: { cashflow: false, delivery: false } },
  pro:   { label: 'Pro', pagesPerWeek: 50, maxInstitutions: 2, monthlyCents: 900, annualCents: 9900, features: { cashflow: true, delivery: true } },
  payg:  { label: 'À la carte', pagesPerWeek: null, maxInstitutions: null, monthlyCents: 400, metered: true, features: { cashflow: true, delivery: true } },
};
// Per-tier feature bullets. Looks up its own translations rather than taking
// a `t` arg — the second parameter is the tier metadata object (label, page
// quota, bank-connection allowance, included features).
function planFeatures(id, cfg) {
  const out = [];
  if (id === 'payg') {
    // À la carte: bullets describe the metered model, not a fixed allowance.
    // "Pick exactly what you need" was removed — the bullets say enough.
    out.push(t('plan.paygPages',    '10 pages/week + $1 for 20 pages/week'));
    out.push(t('plan.paygBanks',    '+$1 per bank connection, includes cash flow forecast'));
    out.push(t('plan.paygDelivery', '+$2 Delivery (DoorDash, UberEats, Grubhub)'));
    return out;
  }
  out.push(t('plan.pagesPerWeek', '{n} pages / week').replace('{n}', cfg.pagesPerWeek));
  if (cfg.maxInstitutions > 0) {
    const key = cfg.maxInstitutions === 1 ? 'plan.bankConnection' : 'plan.bankConnections';
    out.push(t(key, cfg.maxInstitutions === 1 ? '1 bank connection' : '{n} bank connections').replace('{n}', cfg.maxInstitutions));
  } else {
    out.push(t('plan.noBankConnection', 'No bank connection'));
  }
  if (cfg.features?.cashflow) out.push(t('plan.cashFlowForecast', 'Cash flow forecast'));
  if (cfg.features?.delivery) out.push(t('plan.deliveryAnalysis', 'DoorDash / UberEats / Grubhub analysis'));
  const blurb = id === 'basic' ? t('plan.basicBlurb', 'COGS — receipts & invoices')
              : id === 'pro'   ? t('plan.proBlurb',   'COGS + Cash flow + Delivery analysis')
              : null;
  if (blurb) out.push(blurb);
  return out;
}
// Translated tier label that falls back to the bundle's English label.
function planLabel(id, fallback) {
  if (id === 'basic') return t('plan.basic', fallback || 'Basic');
  if (id === 'pro')   return t('plan.pro',   fallback || 'Pro');
  if (id === 'payg')  return t('plan.aLaCarte', fallback || 'À la carte');
  return fallback || id;
}

// ─── Team management (#/account/team) — owner adds read-only viewers ─────────
function TeamScreen({ dark = false, persona = 'owner' }) {
  const [st, setSt] = React.useState({ loading: true });
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [err, setErr] = React.useState(null);
  // Team-viewers add-on (moved here from the retired plan screen): the add-on is
  // what unlocks viewer seats, so it belongs alongside team management.
  const [tier, setTier] = React.useState(null);
  const [teamAddOn, setTeamAddOn] = React.useState(false);
  const [addlUsers, setAddlUsers] = React.useState(0);
  const [seatBusy, setSeatBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setErr(null);
    try {
      const data = await window.kanjo.scale.team.listTeam();
      setSt({ loading: false, ...data });
    } catch (e) {
      if (e.status === 403) setSt({ loading: false, denied: true });
      else setSt({ loading: false, error: String(e?.message || e) });
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    window.kanjo?.scale?.profile?.getProfile?.()
      .then((p) => { if (p) { setTier(p.tier || 'free'); setTeamAddOn(p.teamAddOn === true); setAddlUsers(p.additionalUsers || 0); } })
      .catch(() => {});
  }, []);

  const saveSeats = async (patch) => {
    setSeatBusy(true); setErr(null); setMsg(null);
    try {
      const r = await window.kanjo.scale.profile.saveProfile(patch);
      setTeamAddOn(r.teamAddOn === true); setAddlUsers(r.additionalUsers || 0);
      setMsg('Team add-on updated.');
      await load(); // seat limit changes server-side → refresh
    } catch (e) { setErr('Could not update team add-on — try again.'); }
    finally { setSeatBusy(false); }
  };

  const seatsFull = st.seats && st.seats.used >= st.seats.limit;

  const doInvite = async () => {
    const e = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e) || busy) { setErr('Enter a valid email.'); return; }
    // Book encryption on → sharing is an explicit privacy downgrade: teammates
    // need readable books, so uploads switch to service-readable while any
    // member exists (encryption resumes when the last one is removed).
    try {
      if (await window.kanjo.backup?.booksEncryptionEnabled?.()) {
        const ok = window.confirm(t('team.encConsent',
          'Your books are end-to-end encrypted. Sharing them with a teammate stores them readable to the Kanjo service (still encrypted at rest) so your teammate can load them. Encryption resumes automatically when your last teammate is removed. Continue?'));
        if (!ok) return;
      }
    } catch { /* consent check is best-effort; invite proceeds */ }
    setBusy(true); setErr(null); setMsg(null);
    try {
      await window.kanjo.scale.team.invite(e);
      // Membership flips the blob format (encrypted↔plaintext) — re-upload NOW
      // so the teammate can load the books immediately, not after the next edit.
      try { window.kanjo.backup?.invalidateTeamCache?.(); await window.kanjo.backup?.shareSnapshotNow?.(); } catch { /* next edit re-uploads */ }
      setEmail(''); setMsg(`Invitation sent to ${e}.`);
      await load();
    } catch (ex) {
      setErr(ex.status === 402 ? 'Seat limit reached — upgrade or add seats.' : 'Could not send invite — try again.');
    } finally { setBusy(false); }
  };
  // After revoke/remove the account may be solo again → re-upload so encryption
  // (if enabled) resumes immediately rather than on the next book edit.
  const reshare = async () => { try { window.kanjo.backup?.invalidateTeamCache?.(); await window.kanjo.backup?.shareSnapshotNow?.(); } catch { /* next edit re-uploads */ } };
  const revoke = async (id) => { setBusy(true); try { await window.kanjo.scale.team.revokeInvite(id); await reshare(); await load(); } catch { setErr('Could not revoke.'); } finally { setBusy(false); } };
  const remove = async (sub) => {
    if (!window.confirm('Remove this member? Their access is revoked and their login is deleted.')) return;
    setBusy(true); try { await window.kanjo.scale.team.removeMember(sub); await reshare(); await load(); } catch { setErr('Could not remove member.'); } finally { setBusy(false); }
  };

  return (
    <KShell tab="today">
      <KHeader back title="Team" />
      <div style={{ padding: '4px 20px 40px', display: 'flex', flexDirection: 'column', gap: 22 }}>
        {st.loading && <div style={{ fontSize: 13, color: 'var(--on-dim)' }}>Loading…</div>}
        {st.denied && <Empty title="Owner only" body="Only the account owner can manage the team." />}
        {st.error && <ErrorBanner>{st.error}</ErrorBanner>}

        {!st.loading && !st.denied && !st.error && (
          <>
            <p style={{ fontSize: 13, color: 'var(--on-soft)', margin: 0, lineHeight: 1.5 }}>
              Add up to {st.seats.limit} read-only teammate{st.seats.limit === 1 ? '' : 's'} who can see your books but can't scan, connect a bank, or edit. {st.seats.used}/{st.seats.limit} used.
            </p>

            {/* Team-viewers add-on (unlocks seats) */}
            <section>
              <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>Team viewers add-on</div>
              <div className="card" style={{ padding: 16, background: 'var(--surface-2,#f5f5f7)', borderRadius: 14 }}>
                {tier === 'free' || tier == null ? (
                  <div style={{ fontSize: 13.5, color: 'var(--ink-2,#666)', lineHeight: 1.5 }}>
                    Read-only teammates are a paid add-on. <KLink to="/account/subscribe" asButton><span style={{ color: 'var(--accent,#5b8def)', fontWeight: 600 }}>Subscribe to unlock ›</span></KLink>
                  </div>
                ) : tier === 'payg' ? (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink,#111)' }}>Additional users — $1/mo each</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-2,#666)', margin: '4px 0 12px' }}>Read-only viewers of your books. Up to 3.</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <button onClick={() => saveSeats({ additionalUsers: Math.max(0, addlUsers - 1) })} disabled={seatBusy || addlUsers <= 0}
                        style={{ width: 36, height: 36, borderRadius: 9, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', fontSize: 18, cursor: 'pointer' }}>−</button>
                      <span className="mono" style={{ fontSize: 18, fontWeight: 600, minWidth: 20, textAlign: 'center' }}>{addlUsers}</span>
                      <button onClick={() => saveSeats({ additionalUsers: Math.min(3, addlUsers + 1) })} disabled={seatBusy || addlUsers >= 3}
                        style={{ width: 36, height: 36, borderRadius: 9, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', fontSize: 18, cursor: 'pointer' }}>+</button>
                      <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 600, color: 'var(--ink,#111)' }}>${addlUsers}/mo</span>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => saveSeats({ teamAddOn: !teamAddOn })} disabled={seatBusy}
                    role="switch" aria-checked={teamAddOn}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'transparent', border: 'none', cursor: seatBusy ? 'default' : 'pointer', textAlign: 'left', padding: 0 }}>
                    <span>
                      <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--ink,#111)' }}>Add team viewers (up to 3) — $2/mo</span>
                      <span style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-2,#666)', marginTop: 2 }}>{teamAddOn ? 'On' : 'Off'}</span>
                    </span>
                    <span aria-hidden="true" style={{ width: 44, height: 26, borderRadius: 13, flexShrink: 0, background: teamAddOn ? 'var(--accent,#5b8def)' : 'var(--hairline,#ccc)', position: 'relative', transition: 'background .15s' }}>
                      <span style={{ position: 'absolute', top: 3, left: teamAddOn ? 21 : 3, width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'left .15s' }}/>
                    </span>
                  </button>
                )}
              </div>
            </section>

            {st.seats.limit === 0 ? (
              <div style={{ padding: 14, borderRadius: 12, background: 'var(--surface-2,#f5f5f7)', fontSize: 13, color: 'var(--ink-2,#666)' }}>
                Turn on the add-on above to invite read-only teammates.
              </div>
            ) : (
              <section>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" inputMode="email"
                    placeholder="teammate@email.com" aria-label="Invite email" disabled={seatsFull}
                    style={{ flex: 1, padding: '12px 14px', fontSize: 15, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' }} />
                  <button onClick={doInvite} disabled={busy || seatsFull}
                    style={{ padding: '0 18px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: (busy || seatsFull) ? 'default' : 'pointer', background: seatsFull ? 'var(--surface-3,#eee)' : 'var(--accent,#5b8def)', color: seatsFull ? 'var(--ink-3,#999)' : '#fff' }}>
                    {busy ? '…' : 'Invite'}
                  </button>
                </div>
                {seatsFull && <div style={{ fontSize: 12.5, color: 'var(--on-dim)', marginTop: 6 }}>All seats in use — remove a member or add seats to invite more.</div>}
                {msg && <div style={{ fontSize: 12.5, color: 'var(--positive,#2e9e5b)', marginTop: 6 }}>{msg}</div>}
                {err && <ErrorBanner style={{ fontSize: 12.5, marginTop: 6 }}>{err}</ErrorBanner>}
              </section>
            )}

            {st.members.length > 0 && (
              <section>
                <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>Members</div>
                {st.members.map((m) => (
                  <div key={m.memberSub} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--hairline,#eee)' }}>
                    <div><div style={{ fontSize: 14, color: 'var(--on)' }}>Viewer</div><div style={{ fontSize: 11.5, color: 'var(--on-dim)' }}>added {new Date(m.addedAt).toLocaleDateString()}</div></div>
                    <button onClick={() => remove(m.memberSub)} disabled={busy} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
                  </div>
                ))}
              </section>
            )}

            {st.invites.length > 0 && (
              <section>
                <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', marginBottom: 8 }}>Pending invites</div>
                {st.invites.map((i) => (
                  <div key={i.invitationId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--hairline,#eee)' }}>
                    <div><div style={{ fontSize: 14, color: 'var(--on)' }}>{i.emailHint || 'Invited'}</div><div style={{ fontSize: 11.5, color: 'var(--on-dim)' }}>expires {new Date(i.expiresAt).toLocaleDateString()}</div></div>
                    <button onClick={() => revoke(i.invitationId)} disabled={busy} style={{ background: 'transparent', border: 'none', color: 'var(--on-soft)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Revoke</button>
                  </div>
                ))}
              </section>
            )}
          </>
        )}
      </div>
    </KShell>
  );
}

// ─── Subscribe / free trial (#/account/subscribe) ───────────────────────────
// Location-gated: the billing-address state decides whether monthly+annual,
// annual-only, or unavailable. Everyone eligible gets a 30-day free trial; no
// card and no sales tax during the trial (payment via Stripe comes later).
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],
  ['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],
  ['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],
  ['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],
  ['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],
  ['WI','Wisconsin'],['WY','Wyoming'],
];

function SubscribeScreen({ dark = false, persona = 'owner' }) {
  const [loc, setLoc] = React.useState({ street: '', city: '', state: '', zip: '' });
  const [rate, setRate] = React.useState(null);       // WA DOR result
  const [rateLoading, setRateLoading] = React.useState(false);
  const [summary, setSummary] = React.useState(null);
  const [chosen, setChosen] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [cardStep, setCardStep] = React.useState(null); // { tier, cycle } when collecting a card
  const [subscribed, setSubscribed] = React.useState(null); // tier once active
  const [manageCard, setManageCard] = React.useState(false); // update-card modal
  const [manageMsg, setManageMsg] = React.useState(null); // cancel/update feedback

  // Load the plan summary, reconciled against the authoritative server status.
  // The local summary's planTier comes from the JWT custom claim, which is stale
  // right after subscribing (the token predates the subscription) — so we override
  // billing lifecycle fields with /subscription/status. Without this the "Manage
  // subscription / Cancel plan" card stays hidden until the user re-logs in.
  const loadSummary = React.useCallback(async () => {
    const base = await window.kanjo?.scale?.plan?.summary?.().catch(() => null);
    let merged = base;
    try {
      const s = await window.kanjo?.scale?.subscription?.getStatus?.();
      if (s) {
        // CANCELING = cancelled but paid-through → still shows the Manage card
        // (with auto-renew off) so the user can re-enable before the period ends.
        // PENDING = first charge in flight → treat as active optimistically.
        const STATUS_TO_TIER = { ACTIVE: 'active', PENDING: 'active', CANCELING: 'active', PAST_DUE: 'grace', CANCELED: 'cancelled', TRIAL: 'trial', PAYMENT_REQUIRED: 'lapsed' };
        merged = {
          ...(base || {}),
          planTier: STATUS_TO_TIER[s.status] || base?.planTier || 'trial',
          tier: s.tier || base?.tier || null,
          cycle: s.cycle || null,
          currentPeriodEnd: s.currentPeriodEnd ?? base?.currentPeriodEnd ?? null,
          autoRenew: s.autoRenew !== false,
          finixSubscriptionId: s.finixSubscriptionId || null,
        };
      }
    } catch { /* status unavailable (offline) — fall back to the local summary */ }
    setSummary(merged);
  }, []);

  React.useEffect(() => {
    loadSummary();
    window.kanjo?.scale?.profile?.getProfile?.()
      .then((p) => { if (p?.serviceLocation) setLoc((l) => ({ ...l, ...p.serviceLocation })); })
      .catch(() => {});
    window.kanjo?.auth?.currentClaims?.().then((c) => setEmail(c?.email || '')).catch(() => {});
  }, [loadSummary]);

  const setField = (k, v) => { setLoc((l) => ({ ...l, [k]: v })); setChosen(null); };

  // WA rate preview — look up when WA + street + zip are present (debounced).
  React.useEffect(() => {
    if (loc.state !== 'WA' || !loc.street || !loc.zip) { setRate(null); return; }
    let cancelled = false;
    setRateLoading(true);
    const h = setTimeout(async () => {
      const r = await window.kanjo.scale.tax.lookupRate(loc);
      if (!cancelled) { setRate(r); setRateLoading(false); }
    }, 500);
    return () => { cancelled = true; clearTimeout(h); };
  }, [loc.state, loc.street, loc.city, loc.zip]); // eslint-disable-line

  const billing = window.kanjo?.scale?.billing;
  const plan = window.kanjo?.scale?.plan;
  const evalRes = loc.state && billing ? billing.evaluate({ country: 'US', state: loc.state, city: loc.city, zip: loc.zip }) : null;
  const annualOnly = evalRes?.status === 'annual_only';
  const taxRate = (loc.state === 'WA' && rate?.taxable && rate?.resolved) ? (rate.combinedRate || 0) : 0;
  const D = (c) => { const d = (c || 0) / 100; return `$${Number.isInteger(d) ? d : d.toFixed(2)}`; };

  const choose = async (tier) => {
    setBusy(true);
    try {
      // Persist the service location + resolved WA rate server-side (drives tax
      // on invoices + the quarterly re-verify cron).
      await window.kanjo.scale.profile.saveProfile?.({
        billingState: loc.state,
        serviceLocation: { ...loc, taxLocationCode: rate?.locationCode, taxRate: rate?.combinedRate },
      });
      await window.kanjo.scale.profile.selectPlan?.(tier);
      setChosen(tier);
      // À la carte isn't a fixed Finix plan; record interest only for now.
      if (tier === 'payg') return;
      // Open card collection. Cycle: annual-only states force annual.
      setCardStep({ tier, cycle: annualOnly ? 'annual' : 'monthly' });
    } catch (e) { /* best-effort */ }
    finally { setBusy(false); }
  };

  const refreshSummary = () => loadSummary();
  const onSubscribed = (tier, status) => { setCardStep(null); setSubscribed({ tier, status }); refreshSummary(); };

  // During the free trial the card sheet SAVES the card (mode:'trial' — no
  // charge today; billing starts when the 30 days end). Once the trial is over
  // (or was never in play) subscribing charges the first period immediately.
  const trialActive = !!summary && summary.planTier === 'trial'
    && (!summary.trialStarted || summary.daysLeftTrial > 0);

  // Save the service address on its own (used by subscribed users who moved their
  // restaurant). Re-persists the resolved WA rate so future invoices tax correctly.
  const [addrMsg, setAddrMsg] = React.useState(null);
  const saveAddress = async () => {
    setBusy(true); setAddrMsg(null);
    try {
      await window.kanjo.scale.profile.saveProfile?.({
        billingState: loc.state,
        serviceLocation: { ...loc, taxLocationCode: rate?.locationCode, taxRate: rate?.combinedRate },
      });
      setChosen(null);
      setAddrMsg(loc.state === 'WA' && rate?.resolved ? `Saved. WA tax ${(rate.combinedRate * 100).toFixed(1)}% applies to future invoices.` : 'Address saved.');
    } catch (e) { setAddrMsg(`Couldn't save: ${e?.message || e}`); }
    finally { setBusy(false); }
  };

  const isSubscribed = summary && (summary.planTier === 'active' || summary.planTier === 'grace');
  // No mid-term cancel: the plan runs to the end of the paid period. Auto-renew
  // controls whether we create the next cycle. Off → access until currentPeriodEnd.
  const toggleAutoRenew = async () => {
    const next = !summary?.autoRenew;
    if (!next && !window.confirm("Turn off auto-renew? You'll keep full access until the end of your current period, then it won't renew.")) return;
    setBusy(true); setManageMsg(null);
    try {
      await window.kanjo.scale.subscription.setAutoRenew(next);
      setManageMsg(next ? 'Auto-renew on.' : 'Auto-renew off — your plan ends when the current period does.');
      refreshSummary();
    } catch (e) { setManageMsg(`Couldn't update auto-renew: ${e?.message || e}`); }
    finally { setBusy(false); }
  };

  const baseLine = (tier) => {
    if (!plan || !evalRes) return '';
    const parts = [];
    if (evalRes.billing.includes('monthly')) parts.push(`${D(plan.priceCents(tier, 'monthly'))}/mo`);
    if (evalRes.billing.includes('annual')) parts.push(`${D(plan.priceCents(tier, 'annual', { annualOnly }))}/yr`);
    return parts.join(' · ');
  };
  const card ={ background: 'var(--surface,#fff)', border: '1px solid var(--hairline,#ddd)', borderRadius: 12, padding: 16 };
  const label = { fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--ink-2,#666)', marginBottom: 8 };
  const input = { width: '100%', padding: '12px 14px', fontSize: 15, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' };

  const trialLine = !summary ? ''
    : summary.planTier === 'active' ? "You're subscribed."
    : !summary.trialStarted ? 'Your 4-week free trial starts when you scan your first page — no card needed.'
    : summary.daysLeftTrial > 0 ? `Free trial — ${summary.daysLeftTrial} day${summary.daysLeftTrial === 1 ? '' : 's'} left. No charge during the trial.`
    : 'Your free trial has ended — pick a plan below to keep going.';

  const plans = ['basic', 'pro', ...(evalRes && evalRes.billing.includes('monthly') ? ['payg'] : [])];

  // WA tax preview line under the address.
  const waNote = loc.state !== 'WA' ? null
    : (!loc.street || !loc.zip) ? 'Enter the street address to calculate WA sales tax.'
    : rateLoading ? 'Looking up the WA sales-tax rate…'
    : rate?.resolved ? `${loc.city || 'WA'} — ${(rate.combinedRate * 100).toFixed(1)}% WA sales tax (code ${rate.locationCode}) added at invoicing.`
    : rate ? "We couldn't resolve that exact address — double-check the street and ZIP."
    : '';

  return (
    <KShell dark={dark} tab="today" persona={persona}>
      <KHeader back title="Plans" />
      <div style={{ padding: '8px 16px 24px', display: 'grid', gap: 16 }}>
        {!isSubscribed && (
          <section style={{ ...card, background: 'var(--brand-bg)', borderColor: 'transparent' }}>
            <div style={{ fontSize: 14, color: 'var(--ink,#111)', lineHeight: 1.45 }}>{trialLine}</div>
          </section>
        )}

        {isSubscribed && (
          <section style={card}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Manage subscription</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2,#666)', marginBottom: 12 }}>
              {summary.planTier === 'grace'
                ? 'Your last payment failed — update your card to restore access.'
                : `${summary.tier ? summary.tier[0].toUpperCase() + summary.tier.slice(1) : 'Your plan'} is active.`}
              {summary.currentPeriodEnd ? (summary.autoRenew
                ? ` Renews ${new Date(summary.currentPeriodEnd).toLocaleDateString()}.`
                : ` Access until ${new Date(summary.currentPeriodEnd).toLocaleDateString()}.`) : ''}
            </div>

            {/* Auto-renew toggle (replaces immediate cancel). */}
            <button onClick={toggleAutoRenew} disabled={busy} role="switch" aria-checked={!!summary.autoRenew}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'var(--surface-2,#f5f5f7)', border: '1px solid var(--hairline,#eee)', borderRadius: 10, padding: '12px 14px', cursor: busy ? 'default' : 'pointer', textAlign: 'left', marginBottom: 8 }}>
              <span>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--ink,#111)' }}>Auto-renew</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-2,#666)', marginTop: 2 }}>{summary.autoRenew ? 'On — renews automatically' : "Off — won't renew"}</span>
              </span>
              <span aria-hidden="true" style={{ width: 44, height: 26, borderRadius: 13, flexShrink: 0, background: summary.autoRenew ? 'var(--accent,#5b8def)' : 'var(--hairline,#ccc)', position: 'relative', transition: 'background .15s' }}>
                <span style={{ position: 'absolute', top: 3, left: summary.autoRenew ? 21 : 3, width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'left .15s' }}/>
              </span>
            </button>
            <button onClick={() => setManageCard(true)} disabled={busy}
              style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Update card
            </button>
            {manageMsg && <div style={{ fontSize: 12.5, color: 'var(--ink-2,#666)', marginTop: 10 }}>{manageMsg}</div>}
          </section>
        )}

        {/* Restaurant address — editable while subscribed (e.g. you moved). Sets sales tax. */}
        {isSubscribed && (
          <section style={card}>
            <div style={label}>Restaurant address</div>
            <p style={{ fontSize: 13, color: 'var(--ink-2,#666)', margin: '0 0 10px', lineHeight: 1.45 }}>
              Where Kanjo tracks your restaurant. Update it if you move — it sets your sales tax.
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              <input value={loc.street} onChange={(e) => setField('street', e.target.value)} placeholder="Street address" aria-label="Street address" style={input}/>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
                <input value={loc.city} onChange={(e) => setField('city', e.target.value)} placeholder="City" aria-label="City" style={input}/>
                <select value={loc.state} onChange={(e) => setField('state', e.target.value)} aria-label="State" style={input}>
                  <option value="">State</option>
                  {US_STATES.map(([code]) => <option key={code} value={code}>{code}</option>)}
                </select>
                <input value={loc.zip} onChange={(e) => setField('zip', e.target.value)} placeholder="ZIP" inputMode="numeric" aria-label="ZIP" style={input}/>
              </div>
            </div>
            {waNote && <div style={{ fontSize: 12.5, color: rate?.resolved ? 'var(--positive,#2e9e5b)' : 'var(--ink-2,#666)', marginTop: 10 }}>{waNote}</div>}
            <button onClick={saveAddress} disabled={busy || !loc.street || !loc.zip}
              style={{ width: '100%', marginTop: 12, padding: '11px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: (busy || !loc.street || !loc.zip) ? 'default' : 'pointer', background: 'var(--accent,#5b8def)', color: '#fff', opacity: (busy || !loc.street || !loc.zip) ? 0.6 : 1 }}>
              {busy ? 'Saving…' : 'Save address'}
            </button>
            {addrMsg && <div style={{ fontSize: 12.5, color: 'var(--ink-2,#666)', marginTop: 10 }}>{addrMsg}</div>}
          </section>
        )}

        {!isSubscribed && (<>
        <section style={card}>
          <div style={label}>Restaurant location</div>
          <p style={{ fontSize: 13, color: 'var(--ink-2,#666)', margin: '0 0 10px', lineHeight: 1.45 }}>
            The address of the restaurant Kanjo tracks — not your home, office, accountant, or PO box. It sets your sales tax. (US-only.)
          </p>
          <div style={{ display: 'grid', gap: 8 }}>
            <input value={loc.street} onChange={(e) => setField('street', e.target.value)} placeholder="Street address" aria-label="Street address" style={input}/>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
              <input value={loc.city} onChange={(e) => setField('city', e.target.value)} placeholder="City" aria-label="City" style={input}/>
              <select value={loc.state} onChange={(e) => setField('state', e.target.value)} aria-label="State" style={input}>
                <option value="">State</option>
                {US_STATES.map(([code]) => <option key={code} value={code}>{code}</option>)}
              </select>
              <input value={loc.zip} onChange={(e) => setField('zip', e.target.value)} placeholder="ZIP" inputMode="numeric" aria-label="ZIP" style={input}/>
            </div>
          </div>
          {waNote && <div style={{ fontSize: 12.5, color: rate?.resolved ? 'var(--positive,#2e9e5b)' : 'var(--ink-2,#666)', marginTop: 10 }}>{waNote}</div>}
        </section>

        {evalRes && evalRes.status === 'blocked' && evalRes.reason === 'city_required' && (
          <section style={card}>
            <div style={{ fontSize: 13, color: 'var(--ink-2,#666)', lineHeight: 1.45 }}>
              Enter your city and ZIP to see the plans available at your address.
            </div>
          </section>
        )}

        {evalRes && evalRes.status === 'blocked' && evalRes.reason !== 'city_required' && (
          <section style={{ ...card, borderColor: 'var(--warning,#e0a23b)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink,#111)', marginBottom: 4 }}>Not available here yet</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2,#666)', lineHeight: 1.45 }}>
              {evalRes.reason === 'chicago'
                ? "We can't offer Kanjo in the city of Chicago right now. We're expanding city by city — check back soon."
                : `We can't offer Kanjo in ${US_STATES.find(([c]) => c === loc.state)?.[1] || 'this state'} right now. We're expanding state by state — check back soon.`}
            </div>
          </section>
        )}

        {evalRes && evalRes.status !== 'blocked' && (
          <>
            {annualOnly && <div style={{ fontSize: 12.5, color: 'var(--on-soft)', marginTop: -4 }}>Annual billing only in your state.</div>}
            {plans.map((tier) => {
              const cfg = (plan && plan.TIERS && plan.TIERS[tier]) || PLAN_FALLBACK[tier];
              const price = tier === 'payg' ? t('plan.priceFrom', 'From $4/mo') : baseLine(tier);
              // The "incl. X% WA tax → …" preview belonged at checkout, not on
              // the plan picker — pre-tax prices match what we advertise.
              return (
                <section key={tier} style={card}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink,#111)' }}>{planLabel(tier, cfg.label)}</div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink,#111)', whiteSpace: 'nowrap' }}>{price}</div>
                    </div>
                  </div>
                  <ul style={{ margin: '10px 0 12px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {planFeatures(tier, cfg).map((f, i) => (
                      <li key={i} style={{ fontSize: 13, color: 'var(--ink-2,#666)', display: 'flex', alignItems: 'center', gap: 7 }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="var(--positive,#2e9e5b)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 7.5l3.2 3.2L12 4"/></svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => choose(tier)} disabled={busy}
                          style={{ width: '100%', padding: '11px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: busy ? 'default' : 'pointer', background: chosen === tier ? 'var(--positive,#2e9e5b)' : 'var(--accent,#5b8def)', color: '#fff', opacity: busy ? 0.6 : 1 }}>
                    {chosen === tier ? 'Interest registered ✓' : `Choose ${cfg.label || tier}`}
                  </button>
                </section>
              );
            })}
            <div style={{ fontSize: 11.5, color: 'var(--on-dim)', textAlign: 'center' }}>
              Secure card entry by Finix — your card details never touch Kanjo's servers.{' '}
              <a href={TOS_URL} target="_blank" rel="noopener" style={{ color: 'var(--accent,#5b8def)' }}>{t('subscribe.tosLink', 'Terms of Service')}</a>
            </div>
          </>
        )}
        </>)}
        {subscribed && (
          <section style={{ ...card, background: 'var(--positive-bg)', borderColor: 'transparent' }}>
            {subscribed.status === 'trialing' ? (
              <>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>Card saved ✓</div>
                <div style={{ fontSize: 13, color: 'var(--ink-2,#666)', marginTop: 4 }}>
                  Nothing charged today. Your {subscribed.tier} plan starts automatically when your 30-day trial ends — we'll email you 3 days before.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>You're subscribed 🎉</div>
                <div style={{ fontSize: 13, color: 'var(--ink-2,#666)', marginTop: 4 }}>Your {subscribed.tier} plan is active. A receipt is on its way by email.</div>
              </>
            )}
          </section>
        )}
      </div>
      {cardStep && (
        <CardCollectModal
          tier={cardStep.tier} cycle={cardStep.cycle} email={email} billingState={loc.state} billingCity={loc.city} billingZip={loc.zip}
          annualOnly={annualOnly} canMonthly={!!evalRes?.billing?.includes('monthly')} taxRate={taxRate}
          trialActive={trialActive}
          onClose={() => setCardStep(null)} onDone={onSubscribed}
        />
      )}
      {manageCard && (
        <CardCollectModal
          mode="updateCard" email={email}
          onClose={() => setManageCard(false)}
          onDone={() => { setManageCard(false); setManageMsg('Card updated. Billing will retry automatically.'); refreshSummary(); }}
        />
      )}
    </KShell>
  );
}

// Current Terms-of-Service revision (matches the "Version" line on
// getkanjo.com/terms). Bump when the page materially changes so re-subscribers
// re-accept the new revision.
const TOS_VERSION = '2026-07-13';
const TOS_URL = 'https://getkanjo.com/terms';

// Finix hosted card-field modal. mode 'subscribe' → POST /subscription/create
// (mode:'trial' while the free trial is running — card saved, NO charge today,
// billing starts when the trial ends); mode 'updateCard' → POST
// /subscription/update-card (replace card on file). Subscribing requires an
// explicit Terms-of-Service acceptance (checkbox gates the pay button); the
// acceptance is recorded locally and sent with the create request.
function CardCollectModal({ mode = 'subscribe', tier, cycle: cycle0, email, billingState, billingCity, billingZip, annualOnly, canMonthly, taxRate = 0, trialActive = false, onClose, onDone }) {
  const mountRef = React.useRef(null);
  const formRef = React.useRef(null);
  const [cycle, setCycle] = React.useState(cycle0);
  const [stage, setStage] = React.useState('loading'); // loading | ready | submitting | error
  const [error, setError] = React.useState(null);
  const [tosOk, setTosOk] = React.useState(false);
  const isUpdate = mode === 'updateCard';

  // Price preview (subscribe only). Recomputes with the cycle toggle. Mirrors the
  // server's computeCharge: base from the plan table + WA tax when applicable.
  const D = (c) => { const d = (c || 0) / 100; return `$${Number.isInteger(d) ? d : d.toFixed(2)}`; };
  const baseCents = !isUpdate ? (window.kanjo?.scale?.plan?.priceCents?.(tier, cycle, { annualOnly }) || 0) : 0;
  const taxCents = Math.round(baseCents * (taxRate || 0));
  const totalCents = baseCents + taxCents;
  const per = cycle === 'annual' ? '/yr' : '/mo';

  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const f = await window.kanjo.scale.finixForm.mountCardForm('finix-card-fields');
        if (!live) { f.unmount?.(); return; }
        formRef.current = f;
        setStage('ready');
      } catch (e) { if (live) { setError(String(e?.message || e)); setStage('error'); } }
    })();
    return () => { live = false; formRef.current?.unmount?.(); };
  }, []);

  const pay = async () => {
    if (!formRef.current) return;
    if (!isUpdate && !tosOk) return; // belt & suspenders — the button is disabled too
    setStage('submitting'); setError(null);
    try {
      const token = await formRef.current.tokenize();
      if (isUpdate) {
        await window.kanjo.scale.subscription.updateCard({ token });
        onDone('updated');
      } else {
        const tosAcceptedAt = new Date().toISOString();
        try { localStorage.setItem('kanjoTosAccepted', JSON.stringify({ version: TOS_VERSION, acceptedAt: tosAcceptedAt, email })); } catch { /* private mode */ }
        const res = await window.kanjo.scale.subscription.createSubscription({
          token, email, tier, cycle, billingState, billingCity, billingZip,
          mode: trialActive ? 'trial' : 'charge',
          tosVersion: TOS_VERSION, tosAcceptedAt,
        });
        onDone(tier, res?.status);
      }
    } catch (e) {
      setError(e?.reason ? `${e.message} (${e.reason})` : (e?.message || 'Payment failed'));
      setStage('ready');
    }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 60 };
  const sheet = { width: '100%', maxWidth: 460, background: 'var(--surface,#fff)', color: 'var(--ink,#111)', borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: '20px 18px calc(env(safe-area-inset-bottom,0) + 20px)' };

  return (
    <div role="dialog" aria-modal="true" aria-label="Enter payment card" style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>{isUpdate ? 'Update payment card' : `Subscribe to ${tier}`}</div>
        {!isUpdate && !annualOnly && canMonthly && (
          <div style={{ display: 'flex', gap: 8, margin: '10px 0 14px' }}>
            {['monthly', 'annual'].map((c) => (
              <button key={c} onClick={() => setCycle(c)}
                style={{ flex: 1, padding: '8px', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: cycle === c ? '1.5px solid var(--accent,#5b8def)' : '1px solid var(--hairline,#ddd)',
                  background: cycle === c ? 'var(--surface,#fff)' : 'transparent', color: 'var(--ink,#111)' }}>
                {c === 'monthly' ? 'Monthly' : 'Annual'}
              </button>
            ))}
          </div>
        )}
        {!isUpdate && annualOnly && <div style={{ fontSize: 12.5, color: 'var(--ink-2,#666)', margin: '8px 0 12px' }}>Annual billing in your state.</div>}

        {!isUpdate && baseCents > 0 && (
          <div style={{ background: 'var(--surface-2,#f5f5f7)', borderRadius: 10, padding: '10px 12px', margin: '0 0 12px', fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ink-2,#666)' }}>
              <span>{tier ? tier[0].toUpperCase() + tier.slice(1) : 'Plan'} · {cycle === 'annual' ? 'annual' : 'monthly'}</span><span>{D(baseCents)}</span>
            </div>
            {taxCents > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ink-2,#666)', marginTop: 4 }}>
                <span>WA sales tax ({(taxRate * 100).toFixed(1)}%)</span><span>{D(taxCents)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--hairline,#e5e5e5)' }}>
              <span>Total{per}</span><span>{D(totalCents)}</span>
            </div>
            {trialActive && (
              <div style={{ fontSize: 12, color: 'var(--positive,#2e9e5b)', marginTop: 8, fontWeight: 600 }}>
                Nothing charged today — billing starts when your 30-day free trial ends. We'll email you 3 days before.
              </div>
            )}
          </div>
        )}

        <div id="finix-card-fields" ref={mountRef} style={{ minHeight: 90, margin: '6px 0 12px' }} />
        {stage === 'loading' && <div style={{ fontSize: 13, color: 'var(--ink-3,#999)' }}>Loading secure card form…</div>}
        {error && <ErrorBanner style={{ marginBottom: 10 }}>{error}</ErrorBanner>}

        {!isUpdate && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '2px 0 12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={tosOk} onChange={(e) => setTosOk(e.target.checked)}
              style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0, accentColor: 'var(--accent,#5b8def)' }} />
            <span style={{ fontSize: 12.5, color: 'var(--ink-2,#666)', lineHeight: 1.45 }}>
              {t('subscribe.tosAgree', 'I have read and agree to the')}{' '}
              <a href={TOS_URL} target="_blank" rel="noopener"
                 style={{ color: 'var(--accent,#5b8def)', fontWeight: 600 }}>
                {t('subscribe.tosLink', 'Terms of Service')}
              </a>
              {' — '}{t('subscribe.tosNote', "Kanjo's figures are estimates and must not be used for tax or other filings.")}
            </span>
          </label>
        )}

        <button onClick={pay} disabled={stage !== 'ready' || (!isUpdate && !tosOk)}
          style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 700, cursor: stage === 'ready' && (isUpdate || tosOk) ? 'pointer' : 'default', background: 'var(--accent,#5b8def)', color: '#fff', opacity: stage === 'ready' && (isUpdate || tosOk) ? 1 : 0.6 }}>
          {stage === 'submitting' ? 'Processing…'
            : isUpdate ? 'Save card'
            : trialActive ? 'Save card — no charge today'
            : `Pay ${D(totalCents)} & subscribe`}
        </button>
        <button onClick={onClose} style={{ width: '100%', padding: '10px', marginTop: 8, borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink-2,#666)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        <div style={{ fontSize: 11, color: 'var(--ink-3,#999)', textAlign: 'center', marginTop: 8 }}>Card secured by Finix · PCI-compliant tokenization</div>
      </div>
    </div>
  );
}

Object.assign(window, { AccountScreen, TeamScreen, SubscribeScreen });
